import React, { Dispatch, SetStateAction, useState } from 'react';

import useCurrentUser from '@hooks/useCurrentUser';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, Page, click } from '@tracking';
import { Scope, SortOption, TopRelatedEntity } from '@utils/backend-api/search';

import DeepResearchPromo from './DeepResearchPromo';
import DeepResearchSwitch from './DeepResearchSwitch';
import SearchBar from './SearchBar';
import SearchPills from './SearchPills';
import SearchTabs from './SearchTabs';
import SearchUpgradeModal from './SearchUpgradeModal';
import SortDropdown from './SortDropdown';
import type { SearchPageProps } from './index';

const DEEP_RESEARCH_TOGGLE_CLICK_TEXT = 'Deep Research Toggle';
const SEARCH_LANDING_MOST_POPULAR = 'Most popular searches';
const RELATED_TOPICS = 'Related topics';

export type SearchHeaderProps = Pick<SearchPageProps, 'scope' | 'sort'> & {
  onClearSearch?: () => void;
  onScopeChange?: (scope: Scope) => void;
  onSearchSubmit?: (query: string) => void;
  onSortChange?: (sort: SortOption) => void;
  searchQuery?: string;
  setSearchQuery?: Dispatch<SetStateAction<string>>;
  topRelatedEntities?: TopRelatedEntity[];
  totalResults?: number;
};

const SearchHeader = ({
  onScopeChange,
  onSearchSubmit,
  onSortChange,
  onClearSearch,
  scope,
  searchQuery,
  setSearchQuery,
  sort,
  topRelatedEntities,
  totalResults,
}: SearchHeaderProps) => {
  const isSearchLandingPage = !searchQuery;
  const { currentUser } = useCurrentUser();
  const [isDeepResearchActive, setIsDeepResearchActive] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { Track, trackEvent } = useEventTracking({
    page: Page.Search,
  });

  const handleToggleDeepResearch = () => {
    // if non-pro subscriber and activating deep research, open modal instead
    const clickTarget = currentUser?.is_pro_subscriber
      ? isDeepResearchActive
        ? ClickTarget.DeactivateDeepResearch
        : ClickTarget.ActivateDeepResearch
      : ClickTarget.Open;

    trackEvent(
      click({
        clickTarget: clickTarget,
        clickText: DEEP_RESEARCH_TOGGLE_CLICK_TEXT,
      })
    );

    if (!isDeepResearchActive && !currentUser?.is_pro_subscriber) {
      setIsModalOpen(true);
      return;
    }

    setIsDeepResearchActive((prevValue) => !prevValue);
  };

  return (
    <Track>
      <SearchBar
        isDeepResearchActive={isDeepResearchActive}
        onClearSearch={onClearSearch}
        onSearchSubmit={onSearchSubmit}
        scope={scope}
        searchQuery={searchQuery}
        sort={sort}
        totalResults={totalResults}
      />
      <DeepResearchSwitch
        isDeepResearchActive={isDeepResearchActive}
        onToggle={handleToggleDeepResearch}
      />
      <DeepResearchPromo
        isDeepResearchActive={isDeepResearchActive}
        setSuggestedPrompt={setSearchQuery}
      />
      {(isSearchLandingPage || topRelatedEntities?.length > 0) && (
        <div className="mt-40 sm:mt-72">
          <h2 className="py-8 font-suisse-intl-mono text-overline-lg font-normal">
            {isSearchLandingPage ? SEARCH_LANDING_MOST_POPULAR : RELATED_TOPICS}
          </h2>
          <SearchPills
            handleSearchSubmit={onSearchSubmit}
            isSearchLandingPage={isSearchLandingPage}
            topRelatedEntities={topRelatedEntities}
          />
          {isSearchLandingPage && (
            <h2 className="mb-0 mt-40 w-fit border-0 border-b border-solid border-[currentColor] pb-12 font-suisse-intl-mono text-overline-lg sm:mt-72">
              Recent Articles
            </h2>
          )}
        </div>
      )}
      <div className="flex flex-col items-end justify-between gap-28 sm:flex-row">
        {!isSearchLandingPage && (
          <SearchTabs onScopeChange={onScopeChange} query={searchQuery} scope={scope} />
        )}
        {!isSearchLandingPage && scope === Scope.Articles && (
          <SortDropdown sortDefault={sort} onSortChange={onSortChange} />
        )}
      </div>
      <SearchUpgradeModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </Track>
  );
};

export default SearchHeader;
